import React, { useEffect, useMemo, useState } from "react";

import {
  Button, Flex, Form, Input, Modal,
  Select,
} from "antd";
import { toast } from "react-toastify";
import { createMlQcRuleSpecific, updateMlQcRuleSpecific } from "../../../../actions/qc";

import {
  buildRegularTestsPayload,
  regularTestsToSelectedTests,
  regularTestsValidationRule,
} from "../utils";
import CategorizedTestSelector from "../../../Common/components/CategorizedTestSelector";
import { getUser } from "../../../../utils/helpers";
import "./EditCustomerRulesModal.css";

const REGULAR_TEST_MATCH_SCOPE = "display_name";

const INITIAL_FORM_VALUES = {
  customer_id: "",
  type: "",
  qc_pointers: "",
  regular_tests: [],
  specs: "",
  scope: "",
  match_scope: REGULAR_TEST_MATCH_SCOPE,
};

export default function EditCustomerRulesModal({
  open,
  onCancel,
  editData,
  refetch,
  customerList,
  info,
}) {
  const isEdit = !!editData;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [disableSampleInfo, setDisableSampleInfo] = useState(false);
  const selectedMatchScope = Form.useWatch("match_scope", form) || REGULAR_TEST_MATCH_SCOPE;
  const scopeOptions = Array.isArray(info?.valid_scopes) ? info.valid_scopes : [];
  const matchScopeOptions = Array.isArray(info?.valid_match_scopes) ? info.valid_match_scopes : [];

  useEffect(() => {
    if (editData) {
      const regularTests = editData.sample_info?.regular_tests;
      const regularTestMatchScope = regularTests?.find((test) => test?.match_scope)?.match_scope
        ?? REGULAR_TEST_MATCH_SCOPE;
      form.setFieldsValue({
        customer_id: editData.customer_id ?? INITIAL_FORM_VALUES.customer_id,
        type: editData.sample_info?.sample_type ?? INITIAL_FORM_VALUES.type,
        qc_pointers: editData.sample_info?.qc_pointers ?? INITIAL_FORM_VALUES.qc_pointers,
        regular_tests: regularTestsToSelectedTests(
          regularTests,
          regularTestMatchScope,
        ),
        specs: editData.sample_info?.specs ?? INITIAL_FORM_VALUES.specs,
        scope: editData.scope ?? INITIAL_FORM_VALUES.scope,
        match_scope: regularTestMatchScope,
      });
    }
  }, [editData, form]);

  const onFinish = async (values) => {
    setLoading(true);
    const {
      customer_id, type: sample_type, scope, match_scope, regular_tests, ...rest
    } = values;
    const formMatchScope = match_scope || REGULAR_TEST_MATCH_SCOPE;
    const sample_info = {
      sample_type,
      regular_tests: buildRegularTestsPayload(regular_tests, formMatchScope),
      ...rest,
    };
    const payload = {
      customer_id,
      sample_info,
      scope,
      user_id: getUser().id,
      ...(isEdit && { _id: editData._id }),
    };

    let response;
    if (!isEdit) {
      // create new customer rule
      response = await createMlQcRuleSpecific([payload]);
    } else {
      // edit existing customer rule
      response = await updateMlQcRuleSpecific(payload);
    }

    const successMessage = response.message || (isEdit ? "Customer rule updated" : "Customer rule created");
    const errorMessage = response.message || "Something went wrong!";

    if (response.success) {
      toast.success(successMessage);
      onCancel();
      if (typeof refetch === "function") {
        refetch();
      }
    } else {
      toast.error(errorMessage);
    }
    setLoading(false);
  };

  const onCancelForm = () => {
    form.resetFields();
    onCancel();
  };

  const requiredFormValidation = (message) => ({
    required: true,
    message,
    transform: (value) => value?.trim(),
  });

  const setSelectedScope = (v) => {
    if (isEdit) return;

    if (v === "generic") {
      form.setFieldsValue({
        type: "",
      });
      setDisableSampleInfo(true);
      form.validateFields(["type"]);
    } else if (v === "sample") {
      setDisableSampleInfo(false);
    }
  };

  const initialFormValues = useMemo(() => {
    if (matchScopeOptions && matchScopeOptions.length > 0) {
      return INITIAL_FORM_VALUES;
    }
    return { ...INITIAL_FORM_VALUES, match_scope: "" };
  }, [matchScopeOptions]);

  return (
    <Modal
      centered
      title={!isEdit ? "Add Customer Rule" : "Edit Customer Rule"}
      width={900}
      open={open}
      onCancel={onCancel}
      rootClassName="EditCustomerRulesModal"
      footer={(
        <Flex className="EditCustomerRulesModal__Footer" align="center" justify="flex-end" gap={8}>
          <Button
            type="default"
            onClick={onCancelForm}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            onClick={form.submit}
            loading={loading}
          >
            Save
          </Button>
        </Flex>
      )}
    >
      <Form
        form={form}
        name="customer_rules"
        initialValues={initialFormValues}
        onFinish={onFinish}
        autoComplete="off"
        colon={false}
        layout="vertical"
        rootClassName="EditCustomerRulesModal__Form"
        requiredMark={false}
      >
        <Flex align="center" gap={16} wrap="wrap">
          <Form.Item
            label="Customer ID"
            name="customer_id"
            rules={[requiredFormValidation("Customer ID required")]}
            className="EditCustomerRulesModal__RuleInput"
          >
            <Select
              allowClear
              showSearch
              className="EditCustomerRulesModal__Input"
              popupClassName="CustomerRange_Customer__Menu"
              placeholder="Select customer"
              options={customerList}
              value={selectedCustomer}
              onSelect={(v) => setSelectedCustomer(v)}
              onClear={() => setSelectedCustomer("")}
              filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
              listHeight={400}
              disabled={isEdit}
            />
          </Form.Item>

          <Form.Item
            label="Scope"
            name="scope"
            rules={[requiredFormValidation("Scope required")]}
            className="EditCustomerRulesModal__RuleInput"
          >
            <Select
              className="EditCustomerRulesModal__Input"
              placeholder="Select scope"
              options={scopeOptions}
              onSelect={(v) => setSelectedScope(v)}
              listHeight={400}
              disabled={isEdit}
            />
          </Form.Item>

          <Form.Item
            label="Sample Info"
            name="type"
            rules={disableSampleInfo || isEdit ? [] : [requiredFormValidation("Type required")]}
            className="EditCustomerRulesModal__RuleInput"
          >
            <Input
              placeholder="Sample Info"
              className="EditCustomerRulesModal__Input"
              disabled={isEdit || disableSampleInfo}
            />
          </Form.Item>

          <Form.Item
            label="Match Scope"
            name="match_scope"
            rules={[requiredFormValidation("Match Scope required")]}
            className="EditCustomerRulesModal__RuleInput"
          >
            <Select
              className="EditCustomerRulesModal__Input"
              placeholder="Select match scope"
              options={matchScopeOptions}
              listHeight={400}
              disabled={isEdit}
            />
          </Form.Item>
        </Flex>

        <Form.Item
          label="QC Pointers"
          name="qc_pointers"
          rules={[requiredFormValidation("QC Pointers required")]}
        >
          <Input.TextArea
            rows={3}
            placeholder="QC Pointers"
            className="EditCustomerRulesModal__TextArea"
          />
        </Form.Item>

        <Form.Item
          label="Regular Tests"
          name="regular_tests"
          dependencies={["match_scope"]}
          rules={[regularTestsValidationRule]}
        >
          <CategorizedTestSelector height={360} matchScope={selectedMatchScope} />
        </Form.Item>

        <Form.Item
          label="Specs"
          name="specs"
        >
          <Input.TextArea
            rows={3}
            placeholder="Specs"
            className="EditCustomerRulesModal__TextArea"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
